/**
 * Lightweight, dependency-free toast store (replaces react-toastify).
 *
 * Exposes a drop-in `toast` API so existing call sites keep working:
 *   toast.success("message")
 *   toast.error("message")
 *   toast.warning("message")
 *   toast.info("message")
 *   toast("message")            // default (info)
 *   toast.dismiss(id)           // remove one (or all when no id)
 *
 * Options (2nd arg): { title, duration }
 *   - title:    bold heading (defaults to a per-type label when omitted)
 *   - duration: auto-dismiss ms (default 4000; pass 0 to keep it sticky)
 *
 * The <CustomToaster /> container subscribes to this store and renders the UI.
 */

const listeners = new Set();
let seq = 0;

const DEFAULT_TITLES = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Notification",
};

const notify = (action) => {
  listeners.forEach((fn) => {
    try {
      fn(action);
    } catch (e) {
      /* a broken listener must never break the emitter */
    }
  });
};

/** Subscribe to store actions ({ type: 'add'|'remove'|'clear', toast?, id? }). */
export const subscribeToasts = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const push = (type, message, options = {}) => {
  // Allow passing a React node or a string; coerce nullish to empty string.
  const msg = message == null ? "" : message;
  const id = options.id != null ? options.id : `t_${Date.now()}_${++seq}`;

  // Duration precedence: explicit `duration`, then react-toastify-style
  // `autoClose` (number = ms, false = sticky), else the 4s default.
  let duration = 4000;
  if (typeof options.duration === "number") duration = options.duration;
  else if (typeof options.autoClose === "number") duration = options.autoClose;
  else if (options.autoClose === false) duration = 0;

  const title =
    options.title !== undefined ? options.title : DEFAULT_TITLES[type] || "";

  notify({
    type: "add",
    toast: {
      id,
      type,
      message: msg,
      title,
      duration,
      // Optional click handler (e.g. notification → navigate to the order).
      onClick: typeof options.onClick === "function" ? options.onClick : null,
    },
  });
  return id;
};

const dismiss = (id) => {
  if (id == null) notify({ type: "clear" });
  else notify({ type: "remove", id });
};

// Callable default export mirrors react-toastify's `toast(message)` usage.
function toastFn(message, options) {
  return push("info", message, options);
}

toastFn.success = (message, options) => push("success", message, options);
toastFn.error = (message, options) => push("error", message, options);
toastFn.warning = (message, options) => push("warning", message, options);
toastFn.warn = (message, options) => push("warning", message, options);
toastFn.info = (message, options) => push("info", message, options);
toastFn.dismiss = dismiss;
toastFn.clear = () => dismiss();

export const toast = toastFn;
export default toast;
